/**
 * The input grammar the broker accepts from a phone
 * (packages/broker/controller-policy.js, phoneFrame). The broker drops a
 * frame outside it, so the pad conforms every value before it is sent:
 * a finger far outside the knob, a rounding error or a sensor glitch can
 * never produce a value the broker would refuse.
 *
 *   stick heading   `joyL` / `joyR`   -6284 .. 6284   (radians x 1000)
 *   `-x` / `-y`                       -1000 .. 1000
 *   `-power`                          0 .. 1000
 *   `-thrust`                         0 or 1
 *   `-end` / `-tap`                   exactly 1
 *   buttons                           0 or 1
 *
 * Values are integers in decimal strings.
 */

type SlotInput = (slot: string, value: string) => void;

type Range = readonly [min: number, max: number];

const STICKS = new Set(['joyL', 'joyR']);
const STICK_RANGES: Readonly<Record<string, Range>> = {
  '': [-6284, 6284],
  '-x': [-1000, 1000],
  '-y': [-1000, 1000],
  '-power': [0, 1000],
  '-thrust': [0, 1],
};
/** Suffixes that carry only the value 1: any other value is not an event. */
const EVENT_SUFFIXES = new Set(['-end', '-tap']);

/**
 * Return `value` as a decimal integer string clamped to the range for
 * `slot`, or null when the pair is not something the pad may send at all
 * (an unknown slot or suffix, a non-numeric value, or an event suffix
 * that is not 1).
 */
export function conformInput(slot: string, value: string): string | null {
  const dash = slot.indexOf('-');
  const base = dash < 0 ? slot : slot.slice(0, dash);
  const suffix = dash < 0 ? '' : slot.slice(dash);
  if (slot.length > 16 || typeof value !== 'string' || value.trim() === '') return null;
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  if (EVENT_SUFFIXES.has(suffix)) return STICKS.has(base) && Math.round(number) === 1 ? '1' : null;
  let range: Range | undefined;
  if (STICKS.has(base)) range = Object.prototype.hasOwnProperty.call(STICK_RANGES, suffix) ? STICK_RANGES[suffix] : undefined;
  else if (suffix === '' && base.length > 0) range = [0, 1];
  if (!range) return null;
  const clamped = Math.min(range[1], Math.max(range[0], Math.round(number)));
  // `+ 0` turns -0 into 0 so the string is never "-0".
  return String(clamped + 0);
}

/** Wrap an input sink so every value it receives conforms to the grammar. */
export function conformingInput(send: SlotInput): SlotInput {
  return (slot, value) => {
    const conformed = conformInput(slot, value);
    if (conformed !== null) send(slot, conformed);
  };
}
